import { useState, useEffect } from "react";
import { Palette, Check, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

function hexToRgb(hex) {
  const m = (hex || "").replace("#", "");
  const full = m.length === 3 ? m.split("").map((c) => c + c).join("") : m;
  const n = parseInt(full || "000000", 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
const toHex = (r, g, b) => "#" + [r, g, b].map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("");
const darken = (hex, a = 0.22) => { const [r, g, b] = hexToRgb(hex); return toHex(r * (1 - a), g * (1 - a), b * (1 - a)); };
const lighten = (hex, a = 0.28) => { const [r, g, b] = hexToRgb(hex); return toHex(r + (255 - r) * a, g + (255 - g) * a, b + (255 - b) * a); };

const PRESETS = [
  { key: "orange", name: "Оранжевая", base: "#E85D2A" },
  { key: "blue", name: "Синяя", base: "#1E40AF" },
  { key: "green", name: "Зелёная", base: "#059669" },
  { key: "red", name: "Красная", base: "#DC2626" },
  { key: "purple", name: "Фиолетовая", base: "#7C3AED" },
  { key: "teal", name: "Бирюзовая", base: "#0E7C86" },
];

const DEFAULT_COLOR = "#E85D2A";
const ROOT_VARS = ["--pp-primary", "--pp-primary-hover", "--pp-primary-rgb"];

function previewVars(color) {
  if (!/^#[0-9a-fA-F]{6}$/.test(color)) return;
  const [r, g, b] = hexToRgb(color);
  const root = document.documentElement;
  root.style.setProperty("--pp-primary", color);
  root.style.setProperty("--pp-primary-hover", darken(color, 0.14));
  root.style.setProperty("--pp-primary-rgb", `${r},${g},${b}`);
}
function clearVars() {
  const root = document.documentElement;
  ROOT_VARS.forEach((v) => root.style.removeProperty(v));
}

export default function ColorCustomizer() {
  const { user } = useAuth();
  const { settings, refresh } = useSettings();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState("#E85D2A");
  const [saving, setSaving] = useState(false);

  const current = settings?.theme_primary || "#E85D2A";
  useEffect(() => { setCustom(current); }, [current, open]);
  useEffect(() => () => clearVars(), []);

  if (!user) return null;

  const closeModal = () => { clearVars(); setOpen(false); };

  const setCustomLive = (v) => { setCustom(v); previewVars(v); };

  const apply = async (color) => {
    if (!/^#[0-9a-fA-F]{6}$/.test(color)) { toast.error("Неверный формат цвета (#RRGGBB)"); return; }
    setSaving(true);
    try {
      await api.put("/admin/settings", { theme_primary: color });
      await refresh();
      clearVars();
      setCustom(color);
      toast.success("Цвет сайта сохранён");
    } catch { toast.error("Ошибка сохранения"); }
    finally { setSaving(false); }
  };

  const resetDefault = () => { setCustom(DEFAULT_COLOR); apply(DEFAULT_COLOR); };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="color-customizer-btn"
        className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 bg-[#0F1720] text-white rounded-full pl-3 pr-4 py-2.5 text-sm font-medium shadow-lg hover:bg-slate-800 transition-colors"
        title="Настройка темы сайта (админ)"
      >
        <Palette size={17} /> Тема
      </button>

      <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : closeModal())}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto modal-scroll">
          <DialogHeader><DialogTitle className="font-display text-xl">Настройка темы сайта</DialogTitle></DialogHeader>

          <div className="mt-2 space-y-6">
            <p className="text-xs text-slate-400 -mt-2">Наведите или выберите цвет — изменения сразу видны на сайте. Нажмите «Применить», чтобы сохранить.</p>
            {/* presets */}
            <div>
              <h3 className="text-sm font-display font-bold text-[#0F1720] mb-3">Предустановленные темы</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {PRESETS.map((p) => {
                  const active = current.toLowerCase() === p.base.toLowerCase();
                  return (
                    <button
                      key={p.key}
                      onClick={() => apply(p.base)}
                      onMouseEnter={() => previewVars(p.base)}
                      onMouseLeave={() => previewVars(custom)}
                      disabled={saving}
                      data-testid={`theme-${p.key}`}
                      className={`text-left rounded-xl border-2 p-3 transition-all ${active ? "border-[#0F1720] shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-[#0F1720]">{p.name}</span>
                        {active && <Check size={16} className="text-green-600" />}
                      </div>
                      <div className="flex gap-1.5">
                        {[darken(p.base), p.base, lighten(p.base)].map((c, i) => (
                          <span key={i} className="flex-1 h-8 rounded-md" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* custom */}
            <div>
              <h3 className="text-sm font-display font-bold text-[#0F1720] mb-3">Кастомный цвет</h3>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-400 mb-3">Выберите свой уникальный цвет для темы сайта</p>
                <label className="text-sm text-slate-600">Выберите цвет</label>
                <div className="flex gap-2 mt-1.5">
                  <input type="color" value={/^#[0-9a-fA-F]{6}$/.test(custom) ? custom : "#E85D2A"} onChange={(e) => setCustomLive(e.target.value)} className="w-14 h-11 rounded-lg border border-slate-200 cursor-pointer bg-white p-1" data-testid="theme-custom-picker" />
                  <input value={custom} onChange={(e) => setCustomLive(e.target.value)} placeholder="#1e40af" className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#E85D2A]/30" data-testid="theme-custom-input" />
                  <button onClick={() => apply(custom)} disabled={saving} className="bg-[#0F1720] hover:bg-slate-800 text-white rounded-lg px-5 text-sm font-medium transition-colors" data-testid="theme-apply-btn">Применить</button>
                </div>
              </div>
            </div>

            {/* preview */}
            <div>
              <h3 className="text-sm font-display font-bold text-[#0F1720] mb-3">Предпросмотр</h3>
              <div className="rounded-xl border border-slate-200 p-4 flex flex-wrap items-center gap-3">
                <button className="rounded-full px-5 py-2.5 text-sm font-medium text-white" style={{ backgroundColor: custom }}>Кнопка</button>
                <span className="rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wider text-white" style={{ backgroundColor: custom }}>Метка</span>
                <span className="text-sm font-semibold" style={{ color: custom }}>Текст-акцент</span>
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 pt-4">
              <button onClick={resetDefault} disabled={saving} className="inline-flex items-center gap-2 text-slate-600 hover:text-[#0F1720] rounded-lg px-3 py-2.5 text-sm font-medium transition-colors border border-slate-200 hover:border-slate-300" data-testid="theme-reset-btn">
                <RotateCcw size={15} /> Сбросить к исходному
              </button>
              <button onClick={closeModal} className="bg-[#0F1720] hover:bg-slate-800 text-white rounded-lg px-6 py-2.5 text-sm font-medium transition-colors" data-testid="theme-done-btn">Готово</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
